import { useCallback, useEffect, useRef, useState } from "react";

export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
  /** Optimistically replace data without refetching */
  mutate: (updater: (prev: T | null) => T | null) => void;
}

export function useApi<T>(fetcher: () => Promise<T>, deps: unknown[] = []): ApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetcherRef = useRef(fetcher);
  const hasFetchedOnce = useRef(false);
  fetcherRef.current = fetcher;

  const load = useCallback(() => {
    // Only show loading spinner on initial load, not refetches
    if (!hasFetchedOnce.current) setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((result) => {
        setData(result);
        setLoading(false);
        hasFetchedOnce.current = true;
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Unknown error");
        setLoading(false);
        hasFetchedOnce.current = true;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps is caller-provided, like useEffect's own API
  }, deps);

  useEffect(() => {
    load();
  }, [load]);

  const mutate = useCallback((updater: (prev: T | null) => T | null) => {
    setData(updater);
  }, []);

  return { data, loading, error, refetch: load, mutate };
}
